import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface CustomSelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  /** Меню под триггером (по умолчанию) или над ним */
  placement?: 'bottom' | 'top';
  /** Меню в document.body — не режется overflow у предков (рекомендуется) */
  usePortal?: boolean;
  triggerClassName?: string;
  /** Не давать браузерному переводчику коверкать коды (банки и т.п.) */
  noTranslate?: boolean;
  /** chrome — всегда тёмный (шапка). surface — следует теме страницы. */
  variant?: 'chrome' | 'surface';
}

type MenuBox = { top: number; left: number; width: number; maxHeight: number };

function measureMenuBox(triggerEl: HTMLElement, placement: 'bottom' | 'top'): MenuBox {
  const rect = triggerEl.getBoundingClientRect();
  const gap = 4;
  const pad = 8;
  const left = rect.left;
  const width = rect.width;

  if (placement === 'bottom') {
    const maxHeight = Math.min(256, Math.max(80, window.innerHeight - rect.bottom - gap - pad));
    return {
      top: rect.bottom + gap,
      left,
      width,
      maxHeight,
    };
  }

  const maxHeight = Math.min(256, Math.max(80, rect.top - gap - pad));
  let top = rect.top - gap - maxHeight;
  if (top < pad) top = pad;
  return { top, left, width, maxHeight };
}

export function CustomSelect({
  value,
  options,
  onChange,
  disabled = false,
  className = '',
  menuClassName = '',
  placement = 'bottom',
  usePortal = true,
  triggerClassName = '',
  noTranslate = false,
  variant = 'chrome',
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuBox, setMenuBox] = useState<MenuBox | null>(null);

  const selectedLabel = useMemo(
    () => options.find((item) => item.value === value)?.label ?? options[0]?.label ?? '',
    [options, value],
  );

  const syncPosition = useCallback(() => {
    const el = rootRef.current;
    if (!el || !open) return;
    setMenuBox(measureMenuBox(el, placement));
  }, [open, placement]);

  useLayoutEffect(() => {
    if (!open || disabled) return;
    syncPosition();
  }, [open, disabled, options.length, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => syncPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const isSurface = variant === 'surface';
  const optionButtons = options.map((item) => {
    const active = item.value === value;
    return (
      <button
        key={item.value}
        type="button"
        onClick={() => {
          onChange(item.value);
          setOpen(false);
        }}
        className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
          isSurface
            ? active
              ? 'bg-emerald-50 text-emerald-800 dark:bg-blue-500/30 dark:text-blue-100'
              : 'text-slate-800 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/10'
            : active
              ? 'bg-blue-500/30 text-blue-100'
              : 'text-white/90 hover:bg-white/10'
        }`}
        translate={noTranslate ? 'no' : undefined}
        lang={noTranslate ? 'en' : undefined}
      >
        {item.label}
      </button>
    );
  });

  const listMaxHeight = usePortal && menuBox ? menuBox.maxHeight : 256;

  const menuPanel = (
    <div
      ref={menuRef}
      data-custom-select-menu="true"
      className={`rounded-lg border shadow-xl overflow-hidden backdrop-blur ${
        isSurface
          ? 'border-slate-200 bg-white dark:border-white/15 dark:bg-slate-900/95'
          : 'border-white/15 bg-slate-900/95'
      } ${menuClassName}`}
      style={
        usePortal && menuBox
          ? {
              position: 'fixed',
              top: menuBox.top,
              left: menuBox.left,
              width: menuBox.width,
              zIndex: 9999,
            }
          : undefined
      }
    >
      <div className="overflow-y-auto py-1" style={{ maxHeight: listMaxHeight }}>
        {optionButtons}
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`relative ${className}${noTranslate ? ' notranslate' : ''}`}
      translate={noTranslate ? 'no' : undefined}
      lang={noTranslate ? 'en' : undefined}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={`w-full px-3 py-1.5 text-xs rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2 ${
          isSurface
            ? 'border border-slate-200 bg-white text-slate-900 shadow-inner focus:ring-2 focus:ring-emerald-500/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:focus:ring-emerald-500/20'
            : 'border-0 bg-white/10 text-white focus:ring-2 focus:ring-white/50'
        } ${triggerClassName}`}
      >
        <span className="truncate" translate={noTranslate ? 'no' : undefined} lang={noTranslate ? 'en' : undefined}>
          {selectedLabel}
        </span>
        <svg
          className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.512a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && !disabled && usePortal && menuBox && typeof document !== 'undefined'
        ? createPortal(menuPanel, document.body)
        : null}

      {open && !disabled && !usePortal ? (
        <div
          className={`absolute left-0 z-[80] w-full ${
            placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {menuPanel}
        </div>
      ) : null}
    </div>
  );
}
